/** Phase 3A.1 — Creative Studio (copy + images).
 * Copy tab: POST /ai/copywriting → grid of variants with quality chips + Save-as-Asset.
 * Images tab (Phase 3B.1.3): POST /ai/creative/images → image cards w/ AIRunBadge.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, auth, API_BASE } from "../../lib/api";
import { useT } from "../../lib/i18n";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { CopywritingPanel, AdCreativePanel, CopyEditorPanel }
    from "../../components/app/ContentStudioPanels";
import { Badge } from "../../components/ui/badge";
import { Slider } from "../../components/ui/slider";
import { toast } from "sonner";
import { Image as ImageIcon, Sparkles } from "lucide-react";
import AIRunBadge from "../../components/app/AIRunBadge";
import EmptyState from "../../components/app/EmptyState";
import HeroImageGenerator from "../../components/app/HeroImageGenerator";

const PLATFORMS = ["meta", "facebook", "instagram", "google_search", "linkedin", "tiktok", "youtube", "email"];
const FRAMEWORKS = ["AIDA", "PAS", "BAB", "Authority", "Social Proof", "Urgency"];
const ALLOWED_ROLES = new Set(["owner", "admin", "manager", "producer"]);
const IMAGE_STYLES = ["none", "photo", "illustration", "abstract"];
const IMAGE_ASPECTS = ["1:1", "16:9", "9:16", "4:5"];
const IMAGE_PLATFORMS = ["meta", "instagram", "linkedin", "tiktok", "youtube", "google_search", "landing_page"];

function qualityClass(q) {
  if (q == null) return "";
  if (q >= 0.75) return "bg-emerald-600 text-white border-transparent";
  if (q >= 0.5)  return "bg-amber-500 text-white border-transparent";
  return "bg-red-600 text-white border-transparent";
}

export default function Creative() {
  const t = useT();
  const role = (auth.getUser() || {}).role || "";
  const canUse = ALLOWED_ROLES.has(role);

  const [platform, setPlatform] = useState("meta");
  const [framework, setFramework] = useState("AIDA");
  const [nVariants, setNVariants] = useState(3);
  const [locale, setLocale] = useState("de");
  const [mode, setMode] = useState("brief"); // "brief" | "campaign"
  const [briefText, setBriefText] = useState("");
  const [campaigns, setCampaigns] = useState([]);
  const [campaignId, setCampaignId] = useState("");
  const [personas, setPersonas] = useState([]);
  const [personaId, setPersonaId] = useState("");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [savedIds, setSavedIds] = useState({}); // {idx: asset_id}
  const [history, setHistory] = useState([]);

  useEffect(() => {
    if (!canUse) return;
    api.get("/campaigns").then((r) => setCampaigns((r.data.items || []).slice(0, 50))).catch(() => {});
    api.get("/brand-profile").then((r) => setPersonas(r.data.target_groups || [])).catch(() => {});
    api.get("/agents/runs?limit=10")
      .then((r) => setHistory((r.data.items || []).filter((x) => x.agent_key === "copywriting_agent").slice(0, 10)))
      .catch(() => {});
  }, [canUse]);

  if (!canUse) {
    return (
      <div data-testid="creative-rbac-empty">
        <EmptyState
          title={t("creative.requires_role_title")}
          description={t("creative.requires_role")}
        />
      </div>
    );
  }

  const generate = async () => {
    if (mode === "brief" && !briefText.trim() && !campaignId) {
      toast.error(t("creative.brief_required")); return;
    }
    setRunning(true); setResult(null); setSavedIds({});
    try {
      const body = {
        platform, framework, n_variants: nVariants, locale,
        target_persona_id: personaId || undefined,
      };
      if (mode === "campaign" && campaignId) body.campaign_id = campaignId;
      else if (briefText.trim()) body.campaign_brief = { name: "ad-hoc brief", goal_summary: briefText };
      const r = await api.post("/ai/copywriting", body);
      setResult({ run_id: r.data.run_id, ...(r.data.output || {}) });
      // refresh history
      api.get("/agents/runs?limit=10")
        .then((rr) => setHistory((rr.data.items || []).filter((x) => x.agent_key === "copywriting_agent").slice(0, 10)))
        .catch(() => {});
    } catch (e) {
      toast.error(t("creative.error_generating"));
    } finally { setRunning(false); }
  };

  const copyVariant = async (v) => {
    const text = [v.headline, v.primary_text, v.description, v.cta].filter(Boolean).join("\n\n");
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t("creative.copied"));
    } catch { toast.error(t("creative.copy_failed")); }
  };

  const saveAsAsset = async (v, idx) => {
    if (!result) return;
    try {
      const payload = {
        kind: "copy",
        name: `${v.framework || framework} · ${platform} · #${idx + 1}`,
        text_content: [v.headline, v.primary_text, v.description, v.cta ? `CTA: ${v.cta}` : ""].filter(Boolean).join("\n\n"),
        language: locale,
        tags: [platform, (v.framework || framework).toLowerCase(), "ai-generated"],
        mime_type: "text/plain",
        ai_generated: true,
        source_agent_id: "copywriting_agent",
        source_agent_run_id: result.run_id,
        source_prompt_key: "copywriting.platform_ad.v1",
        source_prompt_version: 1,
        source_llm_provider: "emergent",
        source_llm_model: result.model || "",
        generation_cost_usd: result.cost_usd || 0,
        generation_latency_ms: 0,
        agent_confidence: result.confidence || 0.75,
        quality_score: v.quality_score || 0,
        safety_verdict: v.safety || { approved: true, violations: [] },
        framework: v.framework || framework,
        platform,
      };
      const r = await api.post("/assets", payload);
      setSavedIds({ ...savedIds, [idx]: r.data.id });
      toast.success(t("creative.saved"));
    } catch (e) {
      toast.error(t("creative.save_failed"));
    }
  };

  return (
    <div className="space-y-6" data-testid="creative-page">
      <div>
        <h1 className="text-3xl font-semibold tracking-tight">{t("nav.creative")}</h1>
        <p className="text-sm text-muted-foreground mt-1">{t("creative.subtitle")}</p>
      </div>

      <TopLevelCreativeTabs
        copyForm={
          <>
            <Card>
              <CardContent className="pt-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  <div>
                    <label className="text-xs text-muted-foreground">{t("creative.platform")}</label>
                    <Select value={platform} onValueChange={setPlatform}>
                      <SelectTrigger data-testid="creative-platform"><SelectValue /></SelectTrigger>
                      <SelectContent>{PLATFORMS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">{t("creative.framework")}</label>
                    <Select value={framework} onValueChange={setFramework}>
                      <SelectTrigger data-testid="creative-framework"><SelectValue /></SelectTrigger>
                      <SelectContent>{FRAMEWORKS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">{t("creative.variants")} ({nVariants})</label>
                    <Slider value={[nVariants]} min={1} max={5} step={1}
                            onValueChange={(v) => setNVariants(v[0])} data-testid="creative-nvariants" />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">{t("creative.persona")}</label>
                    <Select value={personaId || "_none"} onValueChange={(v) => setPersonaId(v === "_none" ? "" : v)}>
                      <SelectTrigger data-testid="creative-persona"><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_none">—</SelectItem>
                        {personas.map((p) => <SelectItem key={p.id} value={p.id}>{p.name || p.id}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <Tabs value={mode} onValueChange={setMode}>
                  <TabsList>
                    <TabsTrigger value="brief" data-testid="creative-mode-brief">{t("creative.from_brief")}</TabsTrigger>
                    <TabsTrigger value="campaign" data-testid="creative-mode-campaign">{t("creative.from_campaign")}</TabsTrigger>
                  </TabsList>
                  <TabsContent value="brief" className="mt-3">
                    <Textarea value={briefText} onChange={(e) => setBriefText(e.target.value)} rows={3}
                              placeholder={t("creative.brief_placeholder")} data-testid="creative-brief-text" />
                  </TabsContent>
                  <TabsContent value="campaign" className="mt-3">
                    <Select value={campaignId} onValueChange={setCampaignId}>
                      <SelectTrigger data-testid="creative-campaign-select"><SelectValue placeholder="Select campaign" /></SelectTrigger>
                      <SelectContent>
                        {campaigns.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </TabsContent>
                </Tabs>

                <div className="flex items-center gap-2">
                  <div className="flex gap-2">
                    <Button variant={locale === "de" ? "default" : "outline"} size="sm" onClick={() => setLocale("de")}>DE</Button>
                    <Button variant={locale === "en" ? "default" : "outline"} size="sm" onClick={() => setLocale("en")}>EN</Button>
                  </div>
                  <Button onClick={generate} disabled={running} className="ml-auto" data-testid="creative-generate">
                    {running ? t("creative.generating") : t("creative.generate")}
                  </Button>
                </div>
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-6">
              <div className="lg:col-span-2 space-y-3">
                {result ? (
                  <>
                    <div className="flex items-center justify-between">
                      <h2 className="text-lg font-semibold">{t("creative.variants_title")} ({(result.variants || []).length})</h2>
                      <AIRunBadge model={result.model} cost_usd={result.cost_usd}
                                  latency_ms={undefined} quality_score={
                                    (result.variants || []).reduce((s, v) => s + (v.quality_score || 0), 0) /
                                    Math.max(1, (result.variants || []).length)
                                  } />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {(result.variants || []).map((v, i) => (
                        <Card key={i} data-testid={`creative-variant-${i}`}>
                          <CardContent className="pt-4 space-y-2">
                            <div className="flex items-center gap-2 flex-wrap">
                              <Badge variant="outline">{v.framework || framework}</Badge>
                              <Badge className={`font-mono ${qualityClass(v.quality_score)}`}
                                     data-testid={`creative-variant-q-${i}`}>
                                Q {Number(v.quality_score || 0).toFixed(2)}
                              </Badge>
                              {(v.char_violations || []).length > 0 && (
                                <Badge variant="destructive" data-testid={`creative-variant-cv-${i}`}>
                                  {v.char_violations.length} char limit
                                </Badge>
                              )}
                            </div>
                            <div className="font-semibold">{v.headline}</div>
                            <div className="text-sm">{v.primary_text}</div>
                            {v.description && <div className="text-xs text-muted-foreground">{v.description}</div>}
                            <Badge variant="secondary">CTA: {v.cta}</Badge>
                            <div className="flex items-center gap-2 pt-2 border-t mt-2">
                              <Button size="sm" variant="outline" onClick={() => copyVariant(v)}
                                      data-testid={`creative-copy-${i}`}>
                                {t("creative.copy")}
                              </Button>
                              {savedIds[i] ? (
                                <Badge variant="default" data-testid={`creative-saved-${i}`}>
                                  ✓ {t("creative.saved")} · {savedIds[i].slice(0, 8)}
                                </Badge>
                              ) : (
                                <Button size="sm" onClick={() => saveAsAsset(v, i)}
                                        data-testid={`creative-save-${i}`}>
                                  {t("creative.save_as_asset")}
                                </Button>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </>
                ) : (
                  <Card><CardContent className="py-12 text-center text-sm text-muted-foreground">
                    {t("creative.empty_state")}
                  </CardContent></Card>
                )}
              </div>

              <Card><CardHeader><CardTitle className="text-base">{t("creative.history")}</CardTitle></CardHeader>
                <CardContent className="text-xs space-y-2">
                  {history.length === 0 ? (
                    <div className="text-muted-foreground">{t("creative.history_empty")}</div>
                  ) : history.map((h) => (
                    <div key={h.id} className="flex items-center justify-between border rounded-md px-2 py-1"
                         data-testid={`creative-history-${h.id}`}>
                      <span className="font-mono">{(h.id || "").slice(0, 8)}</span>
                      <Badge variant={h.status === "succeeded" ? "default" : "secondary"}>{h.status}</Badge>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>
          </>
        }
        imagesForm={<ImagesTab campaigns={campaigns} />}
        seoForm={<SEOTab role={role} />}
      />
    </div>
  );
}

function TopLevelCreativeTabs({ copyForm, imagesForm, seoForm }) {
  const t = useT();
  const [sp, setSp] = useSearchParams();
  const initial =
    sp.get("tab") === "images" ? "images" :
    sp.get("tab") === "seo" ? "seo" : "copy";
  const [tab, setTab] = useState(initial);
  useEffect(() => {
    const next = new URLSearchParams(sp);
    if (tab === "images") next.set("tab", "images");
    else if (tab === "seo") next.set("tab", "seo");
    else next.delete("tab");
    setSp(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);
  return (
    <Tabs value={tab} onValueChange={setTab} data-testid="creative-top-tabs">
      <TabsList>
        <TabsTrigger value="copy" data-testid="creative-tab-copy">{t("creative.tabs.copy")}</TabsTrigger>
        <TabsTrigger value="copywriting" data-testid="creative-tab-copywriting">Copywriting</TabsTrigger>
        <TabsTrigger value="ad-creative" data-testid="creative-tab-ad-creative">Ad-Creative</TabsTrigger>
        <TabsTrigger value="copy-editor" data-testid="creative-tab-copy-editor">Copy Editor</TabsTrigger>
        <TabsTrigger value="images" data-testid="creative-tab-images">{t("creative.tabs.images")}</TabsTrigger>
        <TabsTrigger value="seo" data-testid="creative-tab-seo">{t("creative.tabs.seo")}</TabsTrigger>
      </TabsList>
      <TabsContent value="copy" className="mt-4">{copyForm}</TabsContent>
      <TabsContent value="copywriting" className="mt-4"><CopywritingPanel /></TabsContent>
      <TabsContent value="ad-creative" className="mt-4"><AdCreativePanel /></TabsContent>
      <TabsContent value="copy-editor" className="mt-4"><CopyEditorPanel /></TabsContent>
      <TabsContent value="images" className="mt-4">{imagesForm}</TabsContent>
      <TabsContent value="seo" className="mt-4">{seoForm}</TabsContent>
    </Tabs>
  );
}

function useAuthedBlobUrl(assetId) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    if (!assetId) return undefined;
    let revoke = null;
    api.get(`/assets/${assetId}/file`, { responseType: "blob" })
      .then((r) => {
        const url = URL.createObjectURL(r.data);
        revoke = url;
        setSrc(url);
      })
      .catch(() => setSrc(null));
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [assetId]);
  return src;
}

function ImageResultCard({ asset, run, campaignId, onAttached, t }) {
  const blob = useAuthedBlobUrl(asset.id);
  const [attaching, setAttaching] = useState(false);
  const [attached, setAttached] = useState(false);
  const role = (auth.getUser() || {}).role || "";
  const canWriteCampaigns = new Set(["owner", "admin", "manager"]).has(role);

  async function useInCampaign() {
    if (!campaignId) return;
    setAttaching(true);
    try {
      const c = await api.get(`/campaigns/${campaignId}`);
      const existing = c.data?.asset_ids || [];
      if (existing.includes(asset.id)) {
        setAttached(true);
        toast.success(t("creative.images.attached_toast"));
        return;
      }
      await api.put(`/campaigns/${campaignId}`, { asset_ids: [...existing, asset.id] });
      setAttached(true);
      toast.success(t("creative.images.attached_toast"));
      if (onAttached) onAttached(asset.id);
    } catch (e) {
      // Honest fallback: the asset still lives in the library even if attach failed.
      toast.error(t("creative.images.attach_failed"));
    } finally { setAttaching(false); }
  }

  return (
    <Card data-testid={`creative-image-card-${asset.id}`}>
      <CardContent className="pt-4 space-y-3">
        <div className="relative aspect-square w-full bg-muted overflow-hidden rounded-md border">
          {blob ? (
            <img src={blob} alt={t("creative.images.alt")} className="w-full h-full object-cover"
                 data-testid={`creative-image-img-${asset.id}`} />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-muted-foreground">
              <ImageIcon className="size-8" />
            </div>
          )}
        </div>
        <AIRunBadge model={run.model} cost_usd={run.cost_usd}
                    latency_ms={run.latency_ms} quality_score={asset.quality_score} />
        <div className="flex items-center gap-2 flex-wrap">
          <Button asChild variant="outline" size="sm" data-testid={`creative-image-library-${asset.id}`}>
            <Link to="/app/assets">{t("creative.images.open_in_library")}</Link>
          </Button>
          {campaignId && canWriteCampaigns && (
            attached ? (
              <Badge variant="default" data-testid={`creative-image-attached-${asset.id}`}>
                ✓ {t("creative.images.attached_toast")}
              </Badge>
            ) : (
              <Button size="sm" onClick={useInCampaign} disabled={attaching}
                      data-testid={`creative-image-use-${asset.id}`}>
                {attaching ? t("common.loading") : t("creative.images.use_in_campaign")}
              </Button>
            )
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function ImagesTab({ campaigns }) {
  const t = useT();
  // Phase 19a — Hero-Image Generator surfaced at the top of the Images tab.
  const heroLocale = (localStorage.getItem("viona.locale") || "de")
                          .startsWith("en") ? "en" : "de";
  const [prompt, setPrompt] = useState("");
  const [style, setStyle] = useState("none");
  const [aspect, setAspect] = useState("1:1");
  const [nImages, setNImages] = useState(1);
  const [platform, setPlatform] = useState("_none");
  const [campaignId, setCampaignId] = useState("_none");
  const [locale, setLocale] = useState("de");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null); // {assets, model, cost_usd, latency_ms, provider, agent_run_id}
  const [brand, setBrand] = useState(null);

  useEffect(() => {
    api.get("/brand-profile").then((r) => setBrand(r.data)).catch(() => {});
  }, []);

  const selectedCampaign = useMemo(
    () => campaigns.find((c) => c.id === campaignId) || null,
    [campaigns, campaignId],
  );

  function suggestFromCampaign() {
    if (!selectedCampaign) return;
    const headline = selectedCampaign.offer?.headline || selectedCampaign.name || "";
    const voiceTone = brand?.voice?.tone || "";
    const moodWords = (brand?.voice?.keywords || []).slice(0, 4).join(", ");
    const region = (brand?.regions || [])[0]?.name || "";
    const seed = [headline, voiceTone && `tone: ${voiceTone}`,
                  moodWords && `mood: ${moodWords}`, region && `region: ${region}`]
                  .filter(Boolean).join(" — ");
    setPrompt(seed || headline);
  }

  async function generate() {
    if (!prompt.trim()) {
      toast.error(t("creative.images.prompt_required")); return;
    }
    setRunning(true);
    setResult(null);
    try {
      const body = {
        prompt: prompt.trim(),
        aspect_ratio: aspect,
        n_images: nImages,
        locale,
      };
      if (style !== "none") body.style = style;
      if (platform !== "_none") body.platform = platform;
      if (campaignId !== "_none") body.campaign_id = campaignId;
      const r = await api.post("/ai/creative/images", body);
      setResult(r.data);
    } catch (e) {
      const msg = e?.response?.data?.detail?.error || "";
      toast.error(`${t("creative.images.error_generating")}${msg ? ` (${msg})` : ""}`);
    } finally { setRunning(false); }
  }

  void API_BASE;
  return (
    <div className="space-y-4" data-testid="creative-images-tab">
      {/* Phase 19a — Hero-Image Generator surfaced at the top of the Images tab. */}
      <Card data-testid="hero-image-card">
        <CardContent className="pt-6">
          <HeroImageGenerator locale={heroLocale}
                                          campaignId={campaignId !== "_none" ? campaignId : null} />
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-6 space-y-4">
          <div>
            <div className="flex items-baseline justify-between">
              <label className="text-xs text-muted-foreground">{t("creative.images.prompt_label")}</label>
              {selectedCampaign && (
                <Button variant="ghost" size="sm" onClick={suggestFromCampaign}
                        data-testid="creative-images-suggest-btn">
                  <Sparkles className="size-3 mr-1" /> {t("creative.images.suggest_from_campaign")}
                </Button>
              )}
            </div>
            <Textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              rows={3}
              placeholder={t("creative.images.prompt_placeholder")}
              data-testid="creative-images-prompt"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <label className="text-xs text-muted-foreground">{t("creative.images.style_label")}</label>
              <Select value={style} onValueChange={setStyle}>
                <SelectTrigger data-testid="creative-images-style"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {IMAGE_STYLES.map((s) => (
                    <SelectItem key={s} value={s} data-testid={`creative-images-style-${s}`}>
                      {t(`creative.images.style_${s}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">{t("creative.images.aspect_label")}</label>
              <div className="flex gap-1 flex-wrap" data-testid="creative-images-aspect-group">
                {IMAGE_ASPECTS.map((a) => (
                  <Button key={a} size="sm" type="button"
                          variant={aspect === a ? "default" : "outline"}
                          onClick={() => setAspect(a)}
                          data-testid={`creative-images-aspect-${a}`}>{a}</Button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">{t("creative.images.n_images_label")} ({nImages})</label>
              <Slider value={[nImages]} min={1} max={4} step={1}
                      onValueChange={(v) => setNImages(v[0])}
                      data-testid="creative-images-n" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">{t("creative.images.platform_label")}</label>
              <Select value={platform} onValueChange={setPlatform}>
                <SelectTrigger data-testid="creative-images-platform"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">—</SelectItem>
                  {IMAGE_PLATFORMS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
            <div className="md:col-span-2">
              <label className="text-xs text-muted-foreground">{t("creative.images.campaign_label")}</label>
              <Select value={campaignId} onValueChange={setCampaignId}>
                <SelectTrigger data-testid="creative-images-campaign"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">—</SelectItem>
                  {campaigns.map((c) => (
                    <SelectItem key={c.id} value={c.id} data-testid={`creative-images-campaign-opt-${c.id}`}>
                      {c.name || c.id.slice(0, 8)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2 justify-end">
              <Button variant={locale === "de" ? "default" : "outline"} size="sm" onClick={() => setLocale("de")}>DE</Button>
              <Button variant={locale === "en" ? "default" : "outline"} size="sm" onClick={() => setLocale("en")}>EN</Button>
              <Button onClick={generate} disabled={running}
                      data-testid="creative-images-generate">
                {running ? t("creative.images.generating") : t("creative.images.generate_button")}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {running && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="creative-images-skeletons">
          {Array.from({ length: nImages }).map((_, i) => (
            <Card key={i} data-testid={`creative-images-skel-${i}`}>
              <CardContent className="pt-4">
                <div className="aspect-square w-full bg-muted/60 rounded-md animate-pulse" />
                <div className="h-5 mt-3 w-2/3 bg-muted/60 rounded animate-pulse" />
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {!running && result && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="creative-images-results">
          {(result.assets || []).map((a) => (
            <ImageResultCard key={a.id} asset={a}
              run={{ model: result.model, cost_usd: result.cost_usd, latency_ms: result.latency_ms }}
              campaignId={campaignId !== "_none" ? campaignId : null}
              t={t}
            />
          ))}
        </div>
      )}

      {!running && !result && (
        <Card>
          <CardContent className="py-12 text-center text-sm text-muted-foreground" data-testid="creative-images-empty">
            {t("creative.images.empty_state")}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ----- SEO tab (Phase 3B.1.4) ------------------------------------------

const SEO_WRITE_ROLES = new Set(["owner", "admin", "manager"]);

function charCountTone(value, limit) {
  const n = (value || "").length;
  if (n === 0) return "text-muted-foreground";
  if (n > limit) return "text-rose-600";
  if (n >= limit - 10) return "text-amber-600";
  return "text-emerald-600";
}

function CopyableField({ label, value, limit, testid }) {
  const t = useT();
  const n = (value || "").length;
  return (
    <div className="space-y-1" data-testid={`${testid}-block`}>
      <div className="flex items-center justify-between">
        <label className="text-xs text-muted-foreground">{label}</label>
        {limit ? (
          <span className={`text-xs font-mono ${charCountTone(value, limit)}`}
                  data-testid={`${testid}-count`}>
            {n}/{limit}
          </span>
        ) : null}
      </div>
      <div className="flex items-center gap-2">
        <div className={`flex-1 rounded-md border px-2 py-1.5 text-sm bg-muted/40 break-words`}
             data-testid={testid}>{value || "—"}</div>
        <Button size="sm" variant="outline" type="button"
                data-testid={`${testid}-copy`}
                onClick={async () => {
                  try {
                    if (navigator.clipboard && navigator.clipboard.writeText) {
                      await navigator.clipboard.writeText(value || "");
                    }
                    toast.success(t("creative.copied"));
                  } catch (e) { toast.error(t("creative.copy_failed")); }
                }}>{t("creative.seo.copy_button")}</Button>
      </div>
    </div>
  );
}

function SEOTab({ role }) {
  const t = useT();
  const canWrite = SEO_WRITE_ROLES.has(role);
  const [topic, setTopic] = useState("");
  const [primaryKw, setPrimaryKw] = useState("");
  const [secondaryKw, setSecondaryKw] = useState("");
  const [region, setRegion] = useState("");
  const [locale, setLocale] = useState("de");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null); // {output, model, cost_usd, latency_ms, quality_score, agent_run_id}
  const [saving, setSaving] = useState(false);

  async function generate() {
    if (!topic.trim()) {
      toast.error(t("creative.seo.topic_required")); return;
    }
    setRunning(true); setResult(null);
    try {
      const body = {
        topic: topic.trim(),
        primary_keyword: primaryKw || undefined,
        secondary_keywords: secondaryKw.split(",").map((s) => s.trim()).filter(Boolean),
        region: region || undefined,
        locale,
      };
      const r = await api.post("/ai/seo/optimize", body);
      setResult(r.data);
    } catch (e) {
      toast.error(t("creative.seo.error_generating"));
    } finally { setRunning(false); }
  }

  async function saveAsAsset() {
    if (!result) return;
    setSaving(true);
    try {
      const o = result.output || {};
      const text = [
        `Meta Title: ${o.meta_title || ""}`,
        `Meta Description: ${o.meta_description || ""}`,
        `H1: ${o.h1 || ""}`,
        `H2 Options: ${(o.h2_options || []).join(" | ")}`,
        `Primary Keyword: ${o.primary_keyword || ""}`,
        `Secondary Keywords: ${(o.secondary_keywords || []).join(", ")}`,
        `Long-tail: ${(o.long_tail_suggestions || []).join(", ")}`,
        `Schema: ${o.schema_markup_type || ""}`,
        `Outline:\n${(o.content_outline || []).map((s, i) => `${i + 1}. ${s}`).join("\n")}`,
      ].join("\n\n");
      await api.post("/assets", {
        name: `SEO · ${topic.slice(0, 60)}`,
        kind: "copy", status: "draft",
        text_content: text,
        ai_generated: true,
        source_agent_id: "seo_agent",
        source_agent_run_id: result.agent_run_id,
        source_llm_provider: "emergent",
        source_llm_model: result.model,
        quality_score: result.quality_score,
      });
      toast.success(t("creative.seo.saved_toast"));
    } catch (e) {
      toast.error(t("creative.save_failed"));
    } finally { setSaving(false); }
  }

  if (!canWrite) {
    return (
      <Card><CardContent className="py-12 text-center text-sm text-muted-foreground"
                          data-testid="creative-seo-access-empty">
        {t("creative.seo.access_required")}
      </CardContent></Card>
    );
  }

  const o = result?.output;

  return (
    <div className="space-y-4" data-testid="creative-seo-tab">
      <Card>
        <CardContent className="pt-6 space-y-4">
          <div>
            <label className="text-xs text-muted-foreground">{t("creative.seo.topic_label")}</label>
            <Textarea data-testid="creative-seo-topic" rows={2}
                      value={topic} onChange={(e) => setTopic(e.target.value)}
                      placeholder={t("creative.seo.topic_placeholder")} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-muted-foreground">{t("creative.seo.primary_keyword_label")}</label>
              <Input data-testid="creative-seo-primary-kw" value={primaryKw}
                      onChange={(e) => setPrimaryKw(e.target.value)} />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">{t("creative.seo.secondary_keywords_label")}</label>
              <Input data-testid="creative-seo-secondary-kw" value={secondaryKw}
                      onChange={(e) => setSecondaryKw(e.target.value)}
                      placeholder="keyword1, keyword2" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">{t("creative.seo.region_label")}</label>
              <Input data-testid="creative-seo-region" value={region}
                      onChange={(e) => setRegion(e.target.value)} />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex gap-2">
              <Button variant={locale === "de" ? "default" : "outline"} size="sm" onClick={() => setLocale("de")}>DE</Button>
              <Button variant={locale === "en" ? "default" : "outline"} size="sm" onClick={() => setLocale("en")}>EN</Button>
            </div>
            <Button onClick={generate} disabled={running} className="ml-auto"
                    data-testid="creative-seo-generate">
              {running ? t("creative.seo.generating") : t("creative.seo.generate_button")}
            </Button>
          </div>
        </CardContent>
      </Card>

      {running && (
        <Card data-testid="creative-seo-skeleton">
          <CardContent className="pt-6 space-y-3">
            <div className="h-6 w-2/3 bg-muted/60 rounded animate-pulse" />
            <div className="h-4 w-full bg-muted/60 rounded animate-pulse" />
            <div className="h-4 w-5/6 bg-muted/60 rounded animate-pulse" />
            <div className="h-24 w-full bg-muted/60 rounded animate-pulse" />
          </CardContent>
        </Card>
      )}

      {!running && o && (
        <Card data-testid="creative-seo-result">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm">{t("creative.seo.result_title")}</CardTitle>
              <AIRunBadge model={result.model} cost_usd={result.cost_usd}
                          latency_ms={result.latency_ms} quality_score={result.quality_score} />
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <CopyableField label={t("creative.seo.meta_title_label")}
                           value={o.meta_title} limit={60} testid="creative-seo-meta-title" />
            <CopyableField label={t("creative.seo.meta_description_label")}
                           value={o.meta_description} limit={160} testid="creative-seo-meta-desc" />
            <CopyableField label={t("creative.seo.h1_label")}
                           value={o.h1} testid="creative-seo-h1" />

            <div>
              <label className="text-xs text-muted-foreground">{t("creative.seo.h2_options_label")}</label>
              <ul className="mt-1 space-y-1" data-testid="creative-seo-h2-list">
                {(o.h2_options || []).map((h, i) => (
                  <li key={i} className="flex items-center gap-2" data-testid={`creative-seo-h2-${i}`}>
                    <span className="flex-1 text-sm">• {h}</span>
                    <Button size="sm" variant="outline" type="button"
                            data-testid={`creative-seo-h2-copy-${i}`}
                            onClick={async () => {
                              try { await navigator.clipboard.writeText(h || ""); toast.success(t("creative.copied")); }
                              catch (e) { toast.error(t("creative.copy_failed")); }
                            }}>{t("creative.seo.copy_button")}</Button>
                  </li>
                ))}
                {(o.h2_options || []).length === 0 && <li className="text-sm text-muted-foreground">—</li>}
              </ul>
            </div>

            <div>
              <label className="text-xs text-muted-foreground">{t("creative.seo.keywords_label")}</label>
              <div className="flex items-center gap-2 mt-1 flex-wrap" data-testid="creative-seo-keywords">
                <Badge variant="default" data-testid="creative-seo-primary-kw-chip">{o.primary_keyword || "—"}</Badge>
                {(o.secondary_keywords || []).map((k, i) => (
                  <Badge key={i} variant="outline" data-testid={`creative-seo-secondary-kw-${i}`}>{k}</Badge>
                ))}
              </div>
            </div>

            <div>
              <label className="text-xs text-muted-foreground">{t("creative.seo.long_tail_label")}</label>
              <div className="flex items-center gap-2 mt-1 flex-wrap" data-testid="creative-seo-longtail">
                {(o.long_tail_suggestions || []).map((k, i) => (
                  <Badge key={i} variant="secondary" data-testid={`creative-seo-longtail-${i}`}>{k}</Badge>
                ))}
                {(o.long_tail_suggestions || []).length === 0 && <span className="text-sm text-muted-foreground">—</span>}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground">{t("creative.seo.schema_label")}</label>
                <div className="text-sm font-mono mt-1" data-testid="creative-seo-schema">{o.schema_markup_type || "—"}</div>
              </div>
            </div>

            <div>
              <label className="text-xs text-muted-foreground">{t("creative.seo.outline_label")}</label>
              <ol className="mt-1 list-decimal pl-5 space-y-1 text-sm" data-testid="creative-seo-outline">
                {(o.content_outline || []).map((s, i) => (
                  <li key={i} data-testid={`creative-seo-outline-${i}`}>{s}</li>
                ))}
                {(o.content_outline || []).length === 0 && <li className="text-muted-foreground">—</li>}
              </ol>
            </div>

            <div className="pt-2 border-t flex items-center justify-end">
              <Button size="sm" onClick={saveAsAsset} disabled={saving}
                      data-testid="creative-seo-save-as-asset">
                {saving ? t("common.loading") : t("creative.seo.save_as_asset_button")}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {!running && !o && (
        <Card>
          <CardContent className="py-12 text-center text-sm text-muted-foreground"
                       data-testid="creative-seo-empty">
            {t("creative.seo.empty_state")}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

